import videojs from 'video.js';
import { makeVTT } from './captions/make-vtt.js';
import './captions.css';

const SETTINGS_KEY = 'mediasite-player-captions-track';
const _initialTrack = new WeakMap();
const _localCaptions = new WeakMap();
const _enabled = new WeakMap();

/**
 * The Captions module.
 * @member {CaptionsPlugin} captions
 * @memberof MediasitePlayer#
 */

/**
 * Configures closed captions that may be displayed during playback.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class CaptionsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        _enabled.set(this, true);
        try {
            _initialTrack.set(this, localStorage[SETTINGS_KEY]);
        }
        catch (e) { }

        player.ready(() => {
            this.#updateControlBarButtonVisibility();
            this.on(player, ['useractive', 'userinactive'], this.#handleUserActiveChange);
            this.on(player, 'texttrackchange', this.#handleTextTrackChange);
        });
    }

    /**
    * Sets a list of captions that should be available to the user in the player UI.
    * @param {string} language The language code for the provided captions.
    * @param {string} label A label to use in the UI for this set of captions.
    * @param {VTTCue[]} [captions] An array of VTTCue objects. If null, undefined, or an empty array, captions will be disabled for the given language.
    * @example
    * player.captions.set('en', 'English', [
    *     new VTTCue(10, 12, 'This is a caption starting at 10 seconds and ending at 12 seconds.')
    * ]);
    */
    set(language, label, captions) {
        const tracks = this.player.textTracks();
        const initialTrack = _initialTrack.get(this);

        for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].language === language && tracks[i].kind === 'captions') {
                const oldTrack = tracks[i];
                this.off(oldTrack, ['cuechange', 'modechange'], this.#handleCaptionChange);
                this.player.removeRemoteTextTrack(oldTrack);
                break;
            }
        }

        if (Array.isArray(captions) && captions.length > 0) {
            const captionsAsVTT = makeVTT(captions);
            const track = this.player.addRemoteTextTrack({
                src: URL.createObjectURL(captionsAsVTT),
                kind: 'captions',
                label: label ?? language,
                language: language,
                mode: language === initialTrack ? 'showing' : 'visible'
            });

            /*
             * there's a chance that the iframeAPI will try to get the captions before the URL Object
             * from the src of the track finishes setting up the needed cues for the captions
             * if we directly set the cues of the track, say by looping through the cues and adding them
             * i.e. track.track.addCue(captions[index]);
             * we get a visual bug where the caption settings increase the size of the captions, rather than setting them to a size
             * i.e. setting captions font size to 50% can be repeated and shrinks them by half each time
             * since importing them through the url object formats them differently (I think?)
             * 
             * Thus, we're setting a copy of the captions here for later use if we try to get the captions
             * and the track hasn't finished up setting up the ques through the URL
            */
            _localCaptions.set(this, captions);

            if (language === initialTrack) {
                _initialTrack.delete(this);
            }

            this.on(track, ['cuechange', 'modechange'], this.#handleCaptionChange);
        }
    }

    /**
    * Gets the list of captions that are available for the given language.
    * @param {string} language The language code of the captions to be retrieved.
    * @returns {VTTCue[]}
    * @example
    * const englishCaptions = player.captions.get('en');
    */
    get(language) {
        const tracks = this.player.textTracks();
        const captions = [];
        for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].language === language && tracks[i].kind === 'captions') {
                //if we have captions cues, but aren't set yet through the URL at its src
                //grab the local version of them for the time being
                if (!tracks[i].cues && _localCaptions.get(this).length > 0) {
                    captions.push(...Array.from(_localCaptions.get(this)));
                    break;
                }
                captions.push(...Array.from(tracks[i].cues ?? []));
                break;
            }
        }
        return captions;
    }

    /**
     * Returns an array containing VTTCue objects for the captions that apply to the current playback time, if any.
     * @param {string} language
     * @returns {VTTCue[]}
     * @example
     * const currentlyDisplayedCaptions = player.captions.getCurrentCaptions('en');
     */
    getCurrentCaptions(language) {
        const captions = [];

        if (language !== null) {
            const tracks = Array.from(this.player.textTracks()).filter(track => track.kind === 'captions');
            const languageToCheck = language || tracks[0]?.language; // Fall back to the first language if nothing is passed

            for (const track of Array.from(tracks)) {
                if (track.language === languageToCheck) {
                    if (track.activeCues) {
                        captions.push(...Array.from(track.activeCues));
                    }                    
                }
            }
        }
        
        return captions;
    }

    /**
     * The language of the captions track that should be showing. When this value is a string (for example, <code>'en'</code>), the track with the matching language will be shown. If this value is <code>true</code>, the first available track (if any) will be shown.
     * @type {?(string|boolean)}
     * @example <caption>Showing English captions (if available).</caption>
     * player.captions.visible = 'en';
     */
    set visible(language) {
        const tracks = Array.from(this.player.textTracks()).filter(track => track.kind === 'captions');

        let found = false;
        for (const track of tracks) {
            if (track.language === language || (language === true && !found)) {
                track.mode = 'showing';
                found = true;
            }
            else {
                track.mode = 'hidden';
            }
        }
    }

    get visible() {
        const tracks = Array.from(this.player.textTracks());
        return tracks.find(track => {
            return track.mode === 'showing';
        })?.language ?? null;
    }

    /**
     * Whether or not to enable the captions UI.
     * @type {boolean}
     * @example
     * player.captions.enabled = true;
     */
    get enabled() {
        return _enabled.get(this);
    }

    set enabled(enabled) {
        _enabled.set(this, enabled);
        this.#updateControlBarButtonVisibility();
    }

    /**
     * Grabs a back-up version of the captions, mostly useed for iFrame API
     * @returns {VTTCue[]}
     * @example
     * player.captions.localCaptions();
     */
    get localCaptions() {
        return _localCaptions.get(this);
    }

    set localCaptions(captions) {
        _localCaptions.set(this, captions);
    }

    #updateControlBarButtonVisibility() {
        //console.log("Captions in the captions button", this.player.textTracks()).filter(track => track.kind === 'captions');
        const subsCapsButton = this.player.getDescendant('ControlBar', 'SubsCapsButton');
        if (_enabled.get(this) && Array.from(this.player.textTracks()).filter(track => track.kind === 'captions').length > 0) {
            subsCapsButton.removeClass('vjs-hidden');
        }
        else {
            subsCapsButton.addClass('vjs-hidden');
        }
    }

    #handleCaptionChange() {
        const currentCaptions = this.getCurrentCaptions(this.visible);
        this.player.trigger('captionchanged', currentCaptions);
    }

    #handleUserActiveChange() {
        this.player.getChild('TextTrackDisplay').updateDisplay();
    }

    #handleTextTrackChange() {
        const captionTracks = Array.from(this.player.textTracks()).filter(track => track.kind === 'captions');
        if (captionTracks.length > 0) {
            const currentTrack = captionTracks.find(track => track.mode === 'showing');
            //if the caption track exists and has changed, the captions have changed
            this.#handleCaptionChange();
            localStorage[SETTINGS_KEY] = currentTrack?.language;
        }
    }

    dispose() {
        this.off(this.player, ['useractive', 'userinactive'], this.#handleUserActiveChange);
        this.off(this.player, 'texttrackchange', this.#handleTextTrackChange);
        super.dispose();
    }
}

export default CaptionsPlugin;
